import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { Fragment } from "react";
import Translate from "../../utils/Translate";
import { useState } from "react";
import { tryFormatDate } from "../../utils/dateTimeFormat";
import { QRCodeSVG } from "qrcode.react";

/* Single view */
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import "swiper/css/effect-flip";
import "swiper/css/pagination";
import "swiper/css/navigation";
import { EffectFlip, Pagination, Navigation } from "swiper/modules";
import { Avatar, useMediaQuery } from "@mui/material";
import PreviewCompany from "./PreviewCompany";
import PreviewCompetences from "./PreviewCompetences";
import { useEffect } from "react";
import { Warning } from "@mui/icons-material";
import QRCodePreview from "./QRCodePreview";
import PreviewSkillInfo from "./PreviewSkillInfo";

const _adjustedChars = ["L", "K", "F"];

function getAdjustedMarginLeft(fieldValue, fontSize) {
  const isString = fieldValue !== null && fieldValue !== undefined && typeof fieldValue === "string";
  if (!isString) {
    return 0;
  }

  const firstChar = fieldValue[0];
  const isAdjustedFirstChar = _adjustedChars.includes(firstChar);
  return isAdjustedFirstChar ? -3 * Math.pow(fontSize / 48, 2) : 0;
}

function mapRange(value, fromMin, fromMax, toMin, toMax) {
  const percentage = (value - fromMin) / (fromMax - fromMin);
  return toMin + percentage * (toMax - toMin);
}

function getAdjustedFontSize(fieldName, fieldValue, fontSize) {
  if (fieldName === "LicenseGroupDescription") {
    if (fieldValue.length <= 8) {
      return fontSize;
    } else if (fieldValue.length <= 16) {
      const lengthOver = fieldValue.length - 8;
      const scale = 0.9 - lengthOver * 0.05;
      return fontSize * scale;
    } else {
      return fontSize * 0.5;
    }
  }

  return fontSize;
}

function ActorLicenseSkillList({
  field,
  items,
  primaryFontColor,
  secondaryFontColor,
  showExtraInfo,
  isSmallCard,
  onShowSkillInfo,
}) {
  if (!items || items.length === 0) return "-";

  const primaryFontSize = isSmallCard ? 0.5 * field.fontSize : field.fontSize;
  const secondaryFontSize = primaryFontSize * 0.8;

  return (
    <div className="dlcActorLicenseSkillList">
      {items.map((item, i) => (
        <Fragment key={i}>
          <div
            className={
              "dlcActorLicenseSkillListItem dlcInstructorLicenseBacksideHack" + (showExtraInfo ? " selectable" : "")
            }
            onClick={showExtraInfo ? () => onShowSkillInfo(item) : () => {}}
          >
            <div className="dlcActorLicenseSkillListItemContent">
              <img className="dlcSkillListImage dlcClipImg invertImgColor" src={item.imageUrl} />
              <div>
                <div style={{ fontSize: primaryFontSize, ...(primaryFontColor ? { color: primaryFontColor } : {}) }}>
                  {item.description}
                </div>
                <div
                  style={{ fontSize: secondaryFontSize, ...(secondaryFontColor ? { color: secondaryFontColor } : {}) }}
                >
                  {item.skillRegulation}
                </div>
                <div
                  style={{ fontSize: secondaryFontSize, ...(secondaryFontColor ? { color: secondaryFontColor } : {}) }}
                >
                  {item.gradeDate}
                </div>
              </div>
            </div>
            {showExtraInfo && (
              <InfoOutlinedIcon
                className="dlInfoIcon"
                style={{ ...(primaryFontColor ? { color: primaryFontColor } : {}) }}
              />
            )}
          </div>
          {i < items.length - 1 && (
            <FieldUnderline
              style={field.dividerStyle}
              width={field.dividerWidth}
              parentGap={18}
              color={field.dividerColor}
              isSmallCard={isSmallCard}
            />
          )}
        </Fragment>
      ))}
    </div>
  );
}

function FieldTitle({ field, fontColor, isSmallCard }) {
  if (!field.titleTranslationKey) return null;

  const title = field.skipTitleTranslation ? field.titleTranslationKey : Translate.get(field.titleTranslationKey);
  const altTitle =
    field.altTitleTranslationKey &&
    (field.skipTitleTranslation ? field.altTitleTranslationKey : Translate.get(field.altTitleTranslationKey));

  return (
    <div
      style={{
        display: "flex",
        flexDirection: field.isAltTitleRowOrientation ? "row" : "column",
        alignItems: "flex-start",
        gap: field.isAltTitleRowOrientation ? "3px" : "1px",
        ...(fontColor ? { color: fontColor } : {}),
      }}
    >
      <div>{title}</div>
      {altTitle && <div style={{ fontWeight: "lighter", fontStyle: "italic" }}>{altTitle}</div>}
    </div>
  );
}

function FieldUnderline({ style, width, parentGap, color, isSmallCard }) {
  if (!style) return null;

  // This is used to avoid altering the gap between flex items
  let halfMarginVertical = parentGap * 0.5;
  halfMarginVertical = isSmallCard ? 0.5 * halfMarginVertical : halfMarginVertical;
  const marginTop = `-${halfMarginVertical}px`;
  const marginBottom = `-${halfMarginVertical}px`;

  // These values are based on the margins on the cards in the styles dlcFrontText
  // and dlcBackText, to make the line go all the way to the edge
  const marginLeftPx = 29 * (isSmallCard ? 0.5 : 1.0);
  const marginLeft = style === "left" ? `-${marginLeftPx}px` : null;
  const marginRightPx = (29 + 8) * (isSmallCard ? 0.5 : 1.0); // There's an extra 8px padding on the right side
  const marginRight = style === "right" ? `-${marginRightPx}px` : null;

  const adjustedWidth = style === "left" ? marginLeftPx : marginRightPx;
  const divWidth = `calc(100% + ${adjustedWidth}px)`;

  const borderWidth = (width ?? 1) * (isSmallCard ? 0.5 : 1.0);
  const borderBottom = `${borderWidth}px solid ${color}`;
  const divHeight = `${borderWidth}px`;

  return (
    <div
      style={{
        width: divWidth,
        height: divHeight,
        marginTop,
        marginBottom,
        borderBottom,
        ...(marginLeft ? { marginLeft } : {}),
        ...(marginRight ? { marginRight } : {}),
      }}
    ></div>
  );
}

const Frontside = ({
  bgImg,
  overlayImg,
  overlayAnimationUrl,
  regulation,
  fields,
  primaryFontColor,
  secondaryFontColor,
  isSmallCard,
  patternUrl,
  paddingRight,
  paddingLeft,
}) => {
  //console.log("Cardfront", props);
  let customStyle = {};
  if (paddingLeft) {
    customStyle.paddingLeft = `${paddingLeft * (isSmallCard ? 0.5 : 1.0)}px`;
  }
  if (paddingRight) {
    customStyle.paddingRight = `${paddingRight * (isSmallCard ? 0.5 : 1.0)}px`;
  }

  return (
    <>
      {bgImg ? <img className="dlcBgImg" src={bgImg} /> : <div className="dlcBgImg"></div>}
      {patternUrl && <img className="dlcAnimation" src={patternUrl} />}
      {overlayImg && <img className="dlcBgOverlay" src={overlayImg} />}
      {overlayAnimationUrl && <img className="dlcBgOverlay" src={overlayAnimationUrl} />}
      <div className="dlcFrontText" style={customStyle}>
        {fields.map((field, index) => (
          <Fragment key={index}>
            {field.fieldName === "CompanyLogo" ? (
              field.value &&
              field.value != "-" && (
                <div>
                  <img src={field.value} className="dlcCompanyLogo" />
                </div>
              )
            ) : field.fieldName === "ProfilePicture" ? (
              <div className="dlcProfilePicContainer">
                {field.value && field.value != "-" && <img src={field.value} className="dlcProfilePic" />}
              </div>
            ) : field.fieldName === "ProfilePictureSquare" ? (
              field.value && field.value != "-" && <img src={field.value} className="dlcProfilePicSquare dlcClipImg" />
            ) : field.fieldName === "gap" ? (
              <div className="dlcGap"></div>
            ) : field.fieldName === "fixedGap" ? (
              <div style={{ width: "100%", height: `${field.fontSize * (isSmallCard ? 0.5 : 1.0)}px` }}></div>
            ) : (
              <>
                <div>
                  <FieldTitle field={field} fontColor={secondaryFontColor} isSmallCard={isSmallCard} />
                  <div
                    style={{
                      fontSize: getAdjustedFontSize(
                        field.fieldName,
                        field.value,
                        isSmallCard ? 0.5 * field.fontSize : field.fontSize
                      ),
                      marginLeft: getAdjustedMarginLeft(
                        field.value,
                        isSmallCard ? 0.5 * field.fontSize : field.fontSize
                      ),
                      ...(primaryFontColor ? { color: primaryFontColor } : {}),
                      ...(field.isCapitalized ? { textTransform: "uppercase" } : {}),
                    }}
                    className={field.fieldName === "LicenseGroupDescription" ? "dlcMainTitle" : "dlcHeading1"}
                  >
                    {field.value}
                  </div>
                  <div style={secondaryFontColor ? { color: secondaryFontColor } : {}}>
                    {field.fieldName === "LicenseGroupDescription" &&
                      regulation &&
                      `${Translate.get("BasedOnEducationPlan")} ${regulation}`}
                  </div>
                </div>
                <FieldUnderline
                  style={field.underlineStyle}
                  width={field.underlineWidth}
                  parentGap={26}
                  color={field.underlineColor}
                  isSmallCard={isSmallCard}
                />
              </>
            )}
          </Fragment>
        ))}
      </div>
    </>
  );
};

const Backside = ({
  bgImg,
  overlayImg,
  overlayAnimationUrl,
  fields,
  primaryFontColor,
  secondaryFontColor,
  publicShareUrl,
  showExtraInfo,
  handleOpenCompany,
  handleOpenComp,
  handleShowSkillInfo,
  isSmallCard,
  patternUrl,
  hideQr,
  paddingRight,
  paddingLeft,
}) => {
  let customStyle = {};
  if (paddingLeft) {
    customStyle.paddingLeft = `${paddingLeft * (isSmallCard ? 0.5 : 1.0)}px`;
  }
  if (paddingRight) {
    customStyle.paddingRight = `${paddingRight * (isSmallCard ? 0.5 : 1.0)}px`;
  }

  function renderFieldValue(field) {
    if ((field.fieldName === "CourseCoordinator" || field.fieldName === "ActorLicenseAllSkills") && showExtraInfo) {
      return (
        <div
          className="selectable"
          style={primaryFontColor ? { color: primaryFontColor } : {}}
          onClick={field.fieldName === "CourseCoordinator" ? handleOpenCompany : handleOpenComp}
        >
          <div>{field.value}</div>
          <InfoOutlinedIcon className="dlInfoIcon" />
        </div>
      );
    } else if (
      field.fieldName === "ValidTo" &&
      showField(field.fieldName, field.value) &&
      new Date(field.value) < new Date()
    ) {
      return (
        <div style={primaryFontColor ? { color: primaryFontColor } : {}}>
          <div>{field.value}</div>
          <Warning className="warningColor" />
        </div>
      );
    } else if (field.fieldName === "ActorLicenseSkillList") {
      return (
        <ActorLicenseSkillList
          field={field}
          items={field.value}
          primaryFontColor={primaryFontColor}
          secondaryFontColor={secondaryFontColor}
          showExtraInfo={showExtraInfo}
          isSmallCard={isSmallCard}
          onShowSkillInfo={handleShowSkillInfo}
        />
      );
    } else if (field.fieldName === "fixedGap") {
      return <div style={{ width: "100%", height: `${field.fontSize * (isSmallCard ? 0.5 : 1.0)}px` }}></div>;
    } else {
      return <div style={primaryFontColor ? { color: primaryFontColor } : {}}>{field.value}</div>;
    }
  }

  return (
    <>
      {bgImg ? <img className="dlcBgImg" src={bgImg} /> : <div className="dlcBgImg"></div>}
      {patternUrl && <img className="dlcAnimation" src={patternUrl} />}
      {overlayImg && <img className="dlcBgOverlay" src={overlayImg} />}
      {overlayAnimationUrl && <img className="dlcBgOverlay" src={overlayAnimationUrl} />}
      <div className="dlcBackText" style={customStyle}>
        {fields.map((field, index) => (
          <div key={index} className="dlcBackField">
            <FieldTitle field={field} fontColor={secondaryFontColor} isSmallCard={isSmallCard} />
            {renderFieldValue(field)}
            <FieldUnderline
              style={field.underlineStyle}
              width={field.underlineWidth}
              parentGap={26}
              color={field.underlineColor}
              isSmallCard={isSmallCard}
            />
          </div>
        ))}

        {publicShareUrl && !hideQr && (
          <div className="dlcQrCodeRow">
            <div className="dlcQrAndLabelContainer">
              <div className="dlcQrCodeLabel" style={{ color: primaryFontColor }}>
                {Translate.get("ShareLicenseQRLabel")}
              </div>
              <div className="dlcQrCodeContainer">
                {publicShareUrl !== "preview" ? (
                  <QRCodeSVG id="qr-code" value={publicShareUrl} size={90} level="L" marginSize={1} />
                ) : (
                  <QRCodePreview size={90} />
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

function showField(fieldName, value) {
  switch (fieldName) {
    case "ValidTo":
      return !!value;
    default:
      return true;
  }
}

export default function DigitalLicenseView({
  displayData,
  publicShareUrl,
  allowSwiper,
  company,
  skills,
  showExtraInfo,
  isSmallCard,
  expired,
}) {
  const [showCompanyDialog, setShowCompanyDialog] = useState(false);
  const [showCompetencesDialog, setShowCompetencesDialog] = useState(false);
  const [showSkillInfo, setShowSkillInfo] = useState(false);
  const [frontFields, setFrontFields] = useState([]);
  const [backFields, setBackFields] = useState([]);
  const [frontBgColor, setFrontBgColor] = useState({});
  const [backBgColor, setBackBgColor] = useState({});

  const smallScreen = useMediaQuery("(max-width:750px)");

  useEffect(() => {
    const ff = displayData.frontFields.map((row) => {
      const value = displayData.fieldValues[row.fieldName] ? displayData.fieldValues[row.fieldName] : "-";
      return { ...row, value };
    });
    setFrontFields(ff);

    const bf = displayData.backFields
      .filter((row) => showField(row.fieldName, displayData.fieldValues[row.fieldName]))
      .map((row) => {
        let value = displayData.fieldValues[row.fieldName];
        if (row.fieldName === "ValidFrom" || row.fieldName === "ValidTo") {
          value = tryFormatDate(value);
        }
        if (row.fieldName === "ActorLicenseSkillList" && displayData.actorLicenseSkillList) {
          value = displayData.actorLicenseSkillList.map((x) => ({ ...x, gradeDate: tryFormatDate(x.gradeDate) }));
        }

        value = value ? value : "-";
        return { ...row, value };
      });
    setBackFields(bf);

    setFrontBgColor(displayData?.digitalLicenseColor ? { backgroundColor: displayData.digitalLicenseColor } : {});

    setBackBgColor(displayData?.digitalLicenseColorAlt ? { backgroundColor: displayData.digitalLicenseColorAlt } : {});
  }, [displayData]);

  return (
    <>
      {showCompanyDialog && <PreviewCompany company={company} onClose={() => setShowCompanyDialog(false)} />}
      {showCompetencesDialog && <PreviewCompetences skills={skills} onClose={() => setShowCompetencesDialog(false)} />}
      {showSkillInfo && <PreviewSkillInfo skill={showSkillInfo} onClose={() => setShowSkillInfo(false)} />}
      {smallScreen && allowSwiper ? (
        <Swiper
          effect={"flip"}
          grabCursor={true}
          loop={true}
          //pagination={true}
          //navigation={true}
          modules={[EffectFlip]}
          className="swiperDL"
          initialSlide={0}
        >
          <SwiperSlide className="swiperDLSlide" style={frontBgColor}>
            <Frontside
              fields={frontFields}
              bgImg={displayData.frontBackgroundImageUrl || false}
              overlayImg={displayData.frontOverlayImageUrl || false}
              overlayAnimationUrl={displayData.frontOverlayAnimationUrl}
              expired={expired}
              primaryFontColor={displayData.primaryFontColor}
              secondaryFontColor={displayData.secondaryFontColor}
              regulation={displayData.licenseGroupRegulation}
              patternUrl={displayData.patternUrl}
              paddingLeft={displayData.frontPaddingLeft}
              paddingRight={displayData.frontPaddingRight}
            />
          </SwiperSlide>
          <SwiperSlide className="swiperDLSlide" style={backBgColor}>
            <Backside
              fields={backFields}
              handleOpenCompany={() => setShowCompanyDialog(true)}
              handleOpenComp={() => setShowCompetencesDialog(true)}
              handleShowSkillInfo={(skill) => setShowSkillInfo(skill)}
              bgImg={displayData.backBackgroundImageUrl || false}
              overlayImg={displayData.backOverlayImageUrl || false}
              overlayAnimationUrl={displayData.backOverlayAnimationUrl}
              showExtraInfo={showExtraInfo}
              expired={expired}
              publicShareUrl={publicShareUrl}
              primaryFontColor={displayData.primaryFontColorAlt}
              secondaryFontColor={displayData.secondaryFontColorAlt}
              patternUrl={displayData.patternUrlAlt}
              hideQr={displayData.hideDigitalLicenseQr}
              paddingLeft={displayData.backPaddingLeft}
              paddingRight={displayData.backPaddingRight}
            />
          </SwiperSlide>
        </Swiper>
      ) : (
        <div className={isSmallCard ? "dlcSmallCard" : "dlcCard"}>
          <div style={frontBgColor}>
            <Frontside
              fields={frontFields}
              bgImg={displayData.frontBackgroundImageUrl || false}
              overlayImg={displayData.frontOverlayImageUrl || false}
              overlayAnimationUrl={displayData.frontOverlayAnimationUrl}
              expired={expired}
              primaryFontColor={displayData.primaryFontColor}
              secondaryFontColor={displayData.secondaryFontColor}
              isSmallCard={isSmallCard}
              regulation={displayData.licenseGroupRegulation}
              patternUrl={displayData.patternUrl}
              paddingLeft={displayData.frontPaddingLeft}
              paddingRight={displayData.frontPaddingRight}
            />
          </div>
          <div style={backBgColor}>
            <Backside
              fields={backFields}
              handleOpenCompany={() => setShowCompanyDialog(true)}
              handleOpenComp={() => setShowCompetencesDialog(true)}
              handleShowSkillInfo={(skill) => setShowSkillInfo(skill)}
              bgImg={displayData.backBackgroundImageUrl || false}
              overlayImg={displayData.backOverlayImageUrl || false}
              overlayAnimationUrl={displayData.backOverlayAnimationUrl}
              showExtraInfo={showExtraInfo}
              expired={expired}
              publicShareUrl={publicShareUrl}
              primaryFontColor={displayData.primaryFontColorAlt}
              secondaryFontColor={displayData.secondaryFontColorAlt}
              isSmallCard={isSmallCard}
              patternUrl={displayData.patternUrlAlt}
              hideQr={displayData.hideDigitalLicenseQr}
              paddingLeft={displayData.backPaddingLeft}
              paddingRight={displayData.backPaddingRight}
            />
          </div>
        </div>
      )}
    </>
  );
}
